'use client';

import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { Icon } from '@/app_components/common/Icon';
import { Image } from '@/app_components/common/Image';
import { DataPoint } from '@/app_components/common/widgets/RatesWidget/DataPoint';
import { RatesSavings } from '@/app_components/common/widgets/RatesWidget/RatesSavings';
import { Button } from '@/app_components/ui/Button';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import * as Accordion from '@/app_components/ui/primitives/Accordion';
import {
  formatTerm,
  type RatesInfoOutput,
  type RatesInfoOutputItem,
  type RatesInfoOutputKey,
  type RatesInfoOutputValueWithItem,
  ratesInfoOutput,
} from '@/app_data/widgets/RedVentures/constants';
import type { RatesData } from '@/app_data/widgets/utils';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { getPageViewDataLayerObject, trackEvent } from '@/app_lib/services/Gtm/utils';
import { cn, formatDate } from '@/app_lib/utils/utils';

/** Legacy code (Business logic) */
const appendTtcid = (url: string, postId: number, isMobile = true): string => {
  try {
    const newUrl = new URL(url);
    newUrl.searchParams.append('ttcid', `${isMobile ? 'mobile' : 'desktop'}-${postId.toString()}`);
    return newUrl.toString();
  } catch {
    return url;
  }
};

function mergeObject(obj1: RatesInfoOutput, obj2: RatesInfoOutputItem): RatesInfoOutputValueWithItem {
  const result = {} as RatesInfoOutputValueWithItem;

  for (const key of Object.keys(obj1) as (keyof RatesInfoOutput)[]) {
    result[key] = {
      ...obj1[key],
      ...obj2[key],
    } as RatesInfoOutputValueWithItem[typeof key];
  }

  return result;
}

interface BankCardProps {
  data: RatesData;
  depositAmount: number;
  outputs: RatesInfoOutputKey[];
  postId: number;
}

export const RatesCard = ({ data, depositAmount, outputs, postId }: BankCardProps) => {
  const { advertiser_notes, effective_at, apy, is_paid, ep_url, min_to_open, product, institution } = data;
  const isMobile = useMediaQuery(Breakpoints.Mobile);
  const newUrl = appendTtcid(ep_url, postId, isMobile);
  const router = useRouter();

  const isCd = product.type === 'CD';

  const offerings = useMemo(() => {
    const PercentConversionFactor = 100;

    const offeringsData: RatesInfoOutput = {
      apy: {
        date: formatDate(effective_at),
        dateLabel: 'Effective at date',
        value: apy ? `${apy.toFixed(2)} %` : '',
        valueLabel: 'APY value',
      },
      estEarnings: {
        date: 'Over 1 year',
        dateLabel: 'EST. Earnings value',
        value: `$ ${Math.round(depositAmount * (apy / PercentConversionFactor))}`,
        valueLabel: 'EST. Earnings value',
      },
      featured: {},
      minBalance: {
        value: `$ ${min_to_open}`,
        valueLabel: 'Minimum balance for APY value',
      },
      minDeposit: {
        value: `$ ${min_to_open}`,
        valueLabel: 'Minimum deposit value',
      },
      term: {
        value: formatTerm(product.term || 1),
        valueLabel: 'Term value',
      },
    };

    return mergeObject(offeringsData, ratesInfoOutput);
  }, [apy, depositAmount, effective_at, min_to_open, product.term]);

  const quickLook = (
    <div className='flex flex-col gap-4'>
      <div>
        QUICK LOOK
        <br />
        {institution.reviews.short_summary}
      </div>
      <ButtonV2
        className={cn('w-fit justify-center text-accent font-semibold', !institution.reviews.link && 'hidden')}
        href={institution.reviews.link || '/'}
        isTertiary
        onClick={(e) => {
          e.stopPropagation();
        }}
        target='_blank'
      >
        <span>Read Bank Review</span>
        <Icon
          icon='ButtonRight'
          size='sm'
        />
      </ButtonV2>
    </div>
  );

  const trackClick = () => {
    const pageData = getPageViewDataLayerObject();
    const trackingData = {
      // biome-ignore lint/style/useNamingConvention: <external data>
      contentPlacementCD: 'Recommends Widget',
      eventAction: `${institution.name} click`,
      eventCategory: 'Recommends Widget',
    };
    trackEvent({ ...pageData, ...trackingData });
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Entire div needs to be clickable based on if the url is set
    // biome-ignore lint/nursery/noNoninteractiveElementInteractions: need event handlers
    <div
      className={cn(
        'relative flex w-full flex-col md:items-start items-center gap-4 self-stretch p-4 ring-blue-300 hover:ring-1',
        newUrl && 'cursor-pointer',
      )}
      onClick={() => {
        if (newUrl) {
          trackClick();
          router.push(newUrl);
        }
      }}
      onKeyDown={(e) => {
        if (['Enter', ' '].includes(e.key) && newUrl) {
          e.preventDefault();
          e.stopPropagation();
          trackClick();
          router.push(newUrl);
        }
      }}
    >
      {/* Logo, Data Points, Savings */}
      <div className='md:flex contents justify-between gap-4 self-stretch'>
        {/* Logo and Savings on Mobile */}
        <div className='flex md:contents justify-between self-stretch'>
          {/* Logo */}
          <div className='relative flex shrink grow basis-0 leading-0'>
            <span className='flex h-full items-center bg-white'>
              <Image
                alt={institution.name}
                className='w-[128px]'
                height={50}
                pictureClassName='border-4 border-white'
                src={
                  URL.canParse(institution.logo_url)
                    ? institution.logo_url
                    : `https://www.brimg.net/system/img/inst/${institution.logo_url}`
                }
                width={119}
              />
            </span>
          </div>
          {/* Savings */}
          <RatesSavings
            className='md:hidden'
            institution={institution}
            isCd={isCd}
            product={product}
          />
        </div>

        {/* Data Points */}
        <ul className='grid md:contents w-full grid-cols-2 md:gap-4 gap-x-4 gap-y-2 self-stretch'>
          {outputs
            .filter((output) => output !== 'featured')
            .map((output) => {
              const offering = offerings[output];
              return (
                <DataPoint
                  bottomValue={offering.date}
                  className='shrink grow basis-0'
                  key={`bank_card_${offering.title}`}
                  title={offering.title}
                  tooltip={offering.tooltip?.replace('$DEPOSIT_AMOUNT$', depositAmount.toString())}
                  value={offering.value}
                />
              );
            })}
          <hr className='col-span-2 row-start-2 md:hidden h-[1px] w-full text-helper' />
        </ul>
      </div>

      {/* Divider */}
      <hr className='md:block hidden h-[1px] w-full text-helper' />

      {/* Actions */}
      <Accordion.Root
        className='contents'
        collapsible
        type='single'
      >
        <Accordion.Item
          className='contents'
          value='item-1'
        >
          <div className='flex w-full md:flex-col flex-col items-center md:justify-end gap-2'>
            <div className='md:flex contents w-full md:flex-row'>
              {/* Savings */}
              <RatesSavings
                className='order-0 md:inline-flex hidden'
                institution={institution}
                isCd={isCd}
                product={product}
              />

              {/* Buttons */}
              <div className='flex w-full md:flex-row flex-col items-end justify-end md:gap-4 gap-6 self-end'>
                {/* Offer Details */}
                <div className='md:order-1 flex md:w-fit w-full flex-col gap-4 md:self-start'>
                  <Button
                    className={cn('w-full bg-black', !newUrl && 'hidden')}
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(newUrl);
                      trackClick();
                    }}
                    variant='primary'
                  >
                    Open Account
                  </Button>
                  {institution.reviews.short_summary && (
                    <Accordion.Trigger
                      className='flex gap-2 justify-center w-full items-center self-center text-accent hover:text-primary-hover'
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <span className='uppercase font-semibold hover:underline active:underline'>Offer Details</span>
                      <Icon
                        className='group-data-[state=open]:rotate-180'
                        icon='ButtonDown'
                        size='sm'
                      />
                    </Accordion.Trigger>
                  )}
                  <Accordion.Content
                    asChild
                    className='typography-body-graphik-medium md:hidden w-full font-semibold'
                  >
                    {quickLook}
                  </Accordion.Content>
                </div>

                {/* Promoted Offer */}
                <div className='flex md:w-fit w-full flex-col md:items-end gap-2 self-start'>
                  {is_paid && (
                    <Button
                      className='w-full bg-warning'
                      tabIndex={-1}
                    >
                      Promoted Offer
                    </Button>
                  )}
                  <div className='typography-sm max-w-[180px] self-center text-center md:text-end'>
                    {advertiser_notes}
                  </div>
                </div>
              </div>
            </div>
            <Accordion.Content
              asChild
              className='typography-level-6 order-3 col-span-2 md:block hidden w-full font-semibold'
            >
              {quickLook}
            </Accordion.Content>
          </div>
        </Accordion.Item>
      </Accordion.Root>
    </div>
  );
};
